import React, {useMemo} from 'react';
import {useTranslation} from 'next-i18next';
import {AppleStoreBadge, PlayStoreBadge, StoreBadgeGroup} from '@tempo/core';
import {
	LandingFooter,
	LandingFooterSocialNetworkIcon,
	LandingFooterSocialNetwork,
	LandingFooterBottom,
	LandingFooterLink,
	LandingFooterLinks,
	LandingFooterLinkGroup,
} from '@tempo/landing';
import {FacebookIcon, XIcon, InstagramIcon, YoutubeIcon} from '@tempo/icons';

// Components
import DeezerLogo from '@components/logos/DeezerLogo';

import {
	APPLE_STORE_LINK,
	ARTIST_REMUNERATION_LINK,
	BRAND_PARTNERSHIP,
	BUY_GIFT_LINK,
	CGU_LINK,
	CHARTS_LINK,
	COMMUNITY_LINK,
	CONFIDENTIALITY_LINK,
	CONTACT_LINK,
	DEEZER_FOR_CREATORS_LINK,
	DEVICES_LINK,
	DISCOUNT_LINK,
	DOWNLOAD_APP_LINK,
	EXPLORE_LINK,
	FACEBOOK_LINK,
	FEEDBACK_LINK,
	FLOW_LINK,
	GIFT_LINK,
	GOOGLE_PLAY_LINK,
	HIFI_LINK,
	INSTAGRAM_LINK,
	INVESTORS_LINK,
	JOBS_LINK,
	LYRICS_LINK,
	MUSIC_QUIZ_LINK,
	NEW_LINK,
	NEWS_ROOM_LINK,
	OFFER_LINK,
	OFFLINE_LINK,
	OPEN_SOURCE_LINK,
	PERSONAL_DATA_LINK,
	PURPLE_CLUB_LINK,
	SHAKER_LINK,
	SONG_CATCHER_LINK,
	SUPPORT_LINK,
	THE_BACKSTAGE_LINK,
	TRANSFER_PLAYLIST_LINK,
	TWITTER_LINK,
	YOUTUBE_LINK,
} from '@constants/links';

const Footer = () => {
	const {i18n} = useTranslation('d4c');
	const links = useMemo(() => {
		return {
			usefulLinks: {
				title: 'Useful links',
				links: [
					{
						label: 'Download the Deezer app',
						href: DOWNLOAD_APP_LINK,
					},
					{
						label: 'Offers',
						href: OFFER_LINK,
					},
					{
						label: 'Discount',
						href: DISCOUNT_LINK,
					},
					{
						label: 'Reviews',
						href: FEEDBACK_LINK,
					},
					{
						label: 'Use a promo code',
						href: GIFT_LINK,
					},
					{
						label: 'Buy a gift card',
						href: BUY_GIFT_LINK,
					},
					{
						label: 'FAQ',
						href: SUPPORT_LINK,
					},
					{
						label: 'Contact Deezer',
						href: CONTACT_LINK,
					},
				],
			},
			featuresLinks: {
				title: 'Features',
				links: [
					{
						label: 'Flow',
						href: FLOW_LINK,
					},
					{
						label: 'Song identifier',
						href: SONG_CATCHER_LINK,
					},
					{
						label: 'Transfer your music',
						href: TRANSFER_PLAYLIST_LINK,
					},
					{
						label: 'Listen with lyrics',
						href: LYRICS_LINK,
					},
					{
						label: 'Offline mode',
						href: OFFLINE_LINK,
					},
					{
						label: 'HiFi sound quality',
						href: HIFI_LINK,
					},
					{
						label: 'Shared playlists',
						href: SHAKER_LINK,
					},
					{
						label: 'Play Music Quiz',
						href: MUSIC_QUIZ_LINK,
					},
					{
						label: 'Compatible devices',
						href: DEVICES_LINK,
					},
				],
			},
			liveTheMusicLinks: {
				title: 'Live the Music',
				links: [
					{
						label: 'Explore the catalogue',
						href: EXPLORE_LINK,
					},
					{
						label: 'Top songs',
						href: CHARTS_LINK,
					},
					{
						label: 'New releases',
						href: NEW_LINK,
					},
					{
						label: 'The Backstage - Deezer Blog',
						href: THE_BACKSTAGE_LINK,
					},
					{
						label: 'Deezer Community',
						href: COMMUNITY_LINK,
					},
					{
						label: 'Purple Club',
						href: PURPLE_CLUB_LINK,
					},
				],
			},
			aboutUsLinks: {
				title: 'About us',
				links: [
					{
						label: 'Press & News',
						href: NEWS_ROOM_LINK,
					},
					{
						label: 'Artist-Centric Payment System (ACPS)',
						href: ARTIST_REMUNERATION_LINK,
					},
					{
						label: 'Deezer for Creators',
						href: DEEZER_FOR_CREATORS_LINK,
					},
					{
						label: 'Investors',
						href: INVESTORS_LINK,
					},
					{
						label: 'Brand Partnerships',
						href: BRAND_PARTNERSHIP,
					},
					{
						label: 'Careers',
						href: JOBS_LINK,
					},
				],
			},
			legalLinks: {
				title: 'Legal',
				links: [
					{
						label: 'Terms and Conditions',
						href: CGU_LINK,
					},
					{
						label: 'Privacy policy',
						href: PERSONAL_DATA_LINK,
					},
					{
						label: 'Open Source Software Report',
						href: OPEN_SOURCE_LINK,
					},
					{
						label: 'Vulnerability Disclosure Policy',
						href: CONFIDENTIALITY_LINK,
					},
				],
			},
		};
	}, []);

	return (
		<LandingFooter>
			<StoreBadgeGroup>
				<AppleStoreBadge locale={i18n.language} href={APPLE_STORE_LINK} />
				<PlayStoreBadge locale={i18n.language} href={GOOGLE_PLAY_LINK} />
			</StoreBadgeGroup>

			<LandingFooterLinks>
				{Object.values(links).map((el) => (
					<LandingFooterLinkGroup key={el.title} label={el.title}>
						{el.links.map((el) => (
							<LandingFooterLink key={el.label} href={el.href}>
								{el.label}
							</LandingFooterLink>
						))}
					</LandingFooterLinkGroup>
				))}
			</LandingFooterLinks>

			<LandingFooterBottom
				socialIcons={
					<LandingFooterSocialNetwork>
						<LandingFooterSocialNetworkIcon
							aria-label="facebook"
							href={FACEBOOK_LINK}
							icon={<FacebookIcon />}
						/>
						<LandingFooterSocialNetworkIcon
							aria-label="instagram"
							href={INSTAGRAM_LINK}
							icon={<InstagramIcon />}
						/>
						<LandingFooterSocialNetworkIcon
							aria-label="youtube"
							href={YOUTUBE_LINK}
							icon={<YoutubeIcon />}
						/>
						<LandingFooterSocialNetworkIcon
							aria-label="twitter"
							href={TWITTER_LINK}
							icon={<XIcon />}
						/>
					</LandingFooterSocialNetwork>
				}
				logo={<DeezerLogo />}
				languageSelector={null}
			/>
		</LandingFooter>
	);
};

export default React.memo(Footer);
